// Standalone, dependency-free 5x5 SVG "liveliness" indicator. Not shadcn-vendored
// (no upstream registry entry for this yet) — colocated with the other
// hand-rolled shared/ui primitives (reasoning.tsx, tool-group.tsx).
import { cn } from '@/shared/lib/cn';

export type DotMatrixState =
  | 'idle'
  | 'loading'
  | 'thinking'
  | 'streaming'
  | 'searching'
  | 'syncing'
  | 'connecting'
  | 'waiting'
  | 'uploading'
  | 'downloading'
  | 'listening'
  | 'speaking'
  | 'recording'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'paused'
  | 'stopped'
  | 'offline';

// Each state lights a subset of the 25 dots (row-major, 0-24) and picks an
// animation class. Kept as a lookup table rather than per-state components —
// same data, far less code.
const STATE_DOTS: Record<DotMatrixState, number[]> = {
  idle: [12],
  loading: [6, 7, 8, 11, 13, 16, 17, 18],
  thinking: [2, 6, 10, 12, 14, 18, 22],
  streaming: [10, 11, 12, 13, 14],
  searching: [0, 4, 12, 20, 24],
  syncing: [1, 5, 9, 11, 12, 13, 15, 19, 23],
  connecting: [7, 11, 12, 13, 17],
  waiting: [12],
  uploading: [22, 17, 12, 7, 2],
  downloading: [2, 7, 12, 17, 22],
  listening: [10, 11, 12, 13, 14],
  speaking: [6, 8, 11, 12, 13, 16, 18],
  recording: [12],
  success: [1, 5, 7, 11, 15, 13, 3],
  error: [0, 6, 12, 18, 24, 4, 8, 16, 20],
  warning: [2, 6, 7, 8, 11, 13, 22],
  info: [2, 7, 12, 17, 22],
  paused: [11, 13],
  stopped: [6, 7, 8, 11, 12, 13, 16, 17, 18],
  offline: [],
};

const STATE_ANIMATION: Partial<Record<DotMatrixState, string>> = {
  loading: 'animate-pulse',
  thinking: 'animate-pulse',
  streaming: 'animate-pulse',
  searching: 'animate-ping',
  syncing: 'animate-spin',
  connecting: 'animate-pulse',
  waiting: 'animate-pulse',
  uploading: 'animate-bounce',
  downloading: 'animate-bounce',
  listening: 'animate-pulse',
  speaking: 'animate-pulse',
  recording: 'animate-pulse',
};

const STATE_COLOR: Partial<Record<DotMatrixState, string>> = {
  success: 'text-status-success',
  error: 'text-destructive',
  warning: 'text-status-warning',
  info: 'text-primary',
  offline: 'text-muted-foreground/40',
};

export interface DotMatrixProps {
  state: DotMatrixState;
  className?: string;
  /** Accessible label; defaults to the state name. */
  label?: string;
}

// 5x5 grid of dots rendered as an SVG — pure CSS/SVG, no animation library.
export function DotMatrix({ state, className, label }: DotMatrixProps) {
  const lit = new Set(STATE_DOTS[state]);
  const colorClass = STATE_COLOR[state] ?? 'text-muted-foreground';
  const animationClass = STATE_ANIMATION[state] ?? '';

  return (
    <svg
      viewBox="0 0 20 20"
      className={cn('size-4 shrink-0', colorClass, className)}
      role="img"
      aria-label={label ?? state}
    >
      {Array.from({ length: 25 }, (_, i) => {
        const row = Math.floor(i / 5);
        const col = i % 5;
        const isLit = lit.has(i);
        return (
          <circle
            key={i}
            cx={col * 4 + 2}
            cy={row * 4 + 2}
            r={1.3}
            className={cn('fill-current', isLit ? animationClass : '', isLit ? 'opacity-100' : 'opacity-15')}
          />
        );
      })}
    </svg>
  );
}
